import {
  Alert,
  Box,
  Button,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { MuiAdd, MuiAddCircleOutline, MuiClose, MuiUpload } from "../../icons";
import styles from "./FileDropper.module.css";
import { useWindowDragFileListener } from "./useWindowDragFileListener";
import classNames from "classnames";
import { useDropzone } from "react-dropzone";

export interface FileDropperProps
  extends Pick<
    React.AriaAttributes,
    "aria-label" | "aria-labelledby" | "aria-describedby"
  > {
  multiFile?: boolean;
  files?: File | File[] | null;
  onChange?: (files: File | File[] | undefined) => void;
  description?: string;
}

function toFileList(files?: File | File[] | null): File[] {
  if (files == null) return [];
  return Array.isArray(files) ? files : [files];
}

export const FileDropper: React.FC<FileDropperProps> = ({
  multiFile,
  files,
  onChange,
  description,
  ...ariaProps
}) => {
  const fileList = toFileList(files);

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: multiFile ? undefined : 1,
    onDragEnter: () => setDragOverDropzone(true),
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
    },
    onDragLeave: () => setDragOverDropzone(false),
    onDrop: () => {
      setDraggingFile(false);
      setDragOverDropzone(false);
    },
    onDropAccepted: (acceptedFiles) => {
      if (multiFile) {
        onChange?.([...fileList, ...acceptedFiles]);
      } else {
        onChange?.(acceptedFiles[0]);
      }
    },
    onDropRejected: () => {
      setShowMultifileError(true);
    },
  });

  const [draggingFile, setDraggingFile] = useState(false);
  const [dragOverDropzone, setDragOverDropzone] = useState(false);

  const [showMultifileError, setShowMultifileError] = useState(false);

  useWindowDragFileListener(setDraggingFile);

  const handleClear = () => {
    onChange?.(multiFile ? [] : undefined);
  };

  return (
    <>
      <Stack direction="row" spacing={2}>
        <Box
          flex={1}
          {...getRootProps({
            role: "button",
            "aria-label": multiFile ? "Add files" : "Add a file",
            ...ariaProps,
          })}
          className={classNames(styles.dropzone, {
            [styles.draggingFile]: draggingFile,
            [styles.dragOver]: dragOverDropzone,
          })}
        >
          <input {...getInputProps()} />
          <Box className={styles.dropHereText}>
            {fileList.length === 0 && (
              <Box>
                <MuiAdd /> {`${multiFile ? "Add files" : "Add a file"}`}
                <Typography variant="body2" color="text.secondary">
                  {description}
                </Typography>
              </Box>
            )}
            {fileList.length > 0 && fileList.length <= 3 && (
              <Box>
                <MuiUpload /> {fileList.map((f) => f.name).join(", ")}
              </Box>
            )}
            {fileList.length > 3 && (
              <Box>
                <MuiUpload /> {fileList.length} Files
              </Box>
            )}
          </Box>
        </Box>
        {fileList.length > 0 && (
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClear}
            size="small"
          >
            <MuiClose />
          </Button>
        )}
      </Stack>
      <Snackbar
        open={showMultifileError}
        autoHideDuration={6000}
        onClose={() => setShowMultifileError(false)}
      >
        <Alert
          severity="error"
          onClose={() => setShowMultifileError(false)}
          sx={{ width: "100%" }}
          elevation={4}
        >
          Only one file may be uploaded
        </Alert>
      </Snackbar>
    </>
  );
};
